import { useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { leadService } from '../../services/leadService';
import { oportunidadService } from '../../services/oportunidadService';
import { useAuthStore } from '../../store/authStore';

const MiTrabajo = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  
  // Filtros para leads
  const [filtroLeadEstado, setFiltroLeadEstado] = useState<string>('todos');
  const [filtroLeadAsignado, setFiltroLeadAsignado] = useState<boolean>(true);
  
  // Filtros para oportunidades
  const [filtroOportunidadEstado, setFiltroOportunidadEstado] = useState<string>('activa');
  const [filtroOportunidadEtapa, setFiltroOportunidadEtapa] = useState<number | 'todos'>('todos');
  const [filtroOportunidadAsignado, setFiltroOportunidadAsignado] = useState<boolean>(true);

  // Obtener leads
  const { data: leadsData } = useQuery(
    'leads',
    () => leadService.getAll(1, 1000)
  );

  // Obtener oportunidades
  const { data: oportunidadesData } = useQuery(
    'oportunidades',
    () => oportunidadService.getAll(1, 1000)
  );

  // Obtener etapas del pipeline
  const { data: etapasData } = useQuery(
    'pipeline-etapas',
    () => oportunidadService.getPipelineEtapas()
  );

  const leads = leadsData?.data.leads || [];
  const oportunidades = oportunidadesData?.data.oportunidades || [];
  const etapas = etapasData?.data.etapas || [];

  // Filtrar leads
  const leadsFiltrados = leads.filter(lead => {
    if (filtroLeadAsignado && lead.usuarioAsignadoId !== user?.id) {
      return false;
    }
    if (filtroLeadEstado !== 'todos' && lead.estado !== filtroLeadEstado) {
      return false;
    }
    return true;
  });

  // Filtrar oportunidades
  const oportunidadesFiltradas = oportunidades.filter(oportunidad => {
    if (filtroOportunidadAsignado && oportunidad.usuarioAsignadoId !== user?.id) {
      return false;
    }
    if (filtroOportunidadEstado !== 'todos' && oportunidad.estado !== filtroOportunidadEstado) {
      return false;
    }
    if (filtroOportunidadEtapa !== 'todos' && oportunidad.etapaId !== filtroOportunidadEtapa) {
      return false;
    }
    return true;
  });

  // Calcular métricas de leads
  const totalLeads = leadsFiltrados.length;
  const leadsNuevos = leadsFiltrados.filter(l => l.estado === 'nuevo').length;
  const leadsContactados = leadsFiltrados.filter(l => l.estado === 'contactado').length;
  const leadsCalificados = leadsFiltrados.filter(l => l.estado === 'calificado').length;

  // Calcular métricas de oportunidades
  const totalOportunidades = oportunidadesFiltradas.length;
  const oportunidadesActivas = oportunidadesFiltradas.filter(o => o.estado === 'activa').length;
  const valorPipeline = oportunidadesFiltradas
    .filter(o => o.estado === 'activa')
    .reduce((sum, o) => sum + (typeof o.valor === 'string' ? parseFloat(o.valor) : (o.valor || 0)), 0);

  const estadosLeads = ['todos', 'nuevo', 'contactado', 'calificado', 'descalificado', 'convertido'];
  const estadosOportunidades = ['todos', 'activa', 'ganada', 'perdida', 'cancelada'];

  return (
    <div className="max-w-full mx-auto space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Mi Trabajo</h1>
          <p className="text-gray-600 mt-1">Vista consolidada de leads y oportunidades</p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => navigate('/crm/leads/nuevo')}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            + Nuevo Lead
          </button>
          <button
            onClick={() => navigate('/crm/oportunidades/nueva')}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            + Nueva Oportunidad
          </button>
        </div>
      </div>

      {/* Indicadores y Filtros */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Panel de Leads */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-800">Leads</h2>
            <button
              onClick={() => navigate('/crm/leads')}
              className="text-primary-600 hover:text-primary-700 text-sm font-medium"
            >
              Ver todos →
            </button>
          </div>

          {/* Indicadores de Leads */}
          <div className="grid grid-cols-4 gap-2 mb-4">
            <div
              onClick={() => setFiltroLeadEstado('todos')}
              className={`p-2 rounded-lg cursor-pointer transition ${
                filtroLeadEstado === 'todos' ? 'bg-primary-100 border-2 border-primary-500' : 'bg-gray-50 border-2 border-transparent'
              }`}
            >
              <p className="text-xs text-gray-600">Total</p>
              <p className="text-lg font-bold text-gray-800">{totalLeads}</p>
            </div>
            <div
              onClick={() => setFiltroLeadEstado('nuevo')}
              className={`p-2 rounded-lg cursor-pointer transition ${
                filtroLeadEstado === 'nuevo' ? 'bg-blue-100 border-2 border-blue-500' : 'bg-gray-50 border-2 border-transparent'
              }`}
            >
              <p className="text-xs text-gray-600">Nuevos</p>
              <p className="text-lg font-bold text-blue-600">{leadsNuevos}</p>
            </div>
            <div
              onClick={() => setFiltroLeadEstado('contactado')}
              className={`p-2 rounded-lg cursor-pointer transition ${
                filtroLeadEstado === 'contactado' ? 'bg-yellow-100 border-2 border-yellow-500' : 'bg-gray-50 border-2 border-transparent'
              }`}
            >
              <p className="text-xs text-gray-600">Contactados</p>
              <p className="text-lg font-bold text-yellow-600">{leadsContactados}</p>
            </div>
            <div
              onClick={() => setFiltroLeadEstado('calificado')}
              className={`p-2 rounded-lg cursor-pointer transition ${
                filtroLeadEstado === 'calificado' ? 'bg-green-100 border-2 border-green-500' : 'bg-gray-50 border-2 border-transparent'
              }`}
            >
              <p className="text-xs text-gray-600">Calificados</p>
              <p className="text-lg font-bold text-green-600">{leadsCalificados}</p>
            </div>
          </div>

          {/* Filtros adicionales */}
          <div className="flex items-center gap-4 mb-4 pb-4 border-b">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={filtroLeadAsignado}
                onChange={(e) => setFiltroLeadAsignado(e.target.checked)}
                className="rounded"
              />
              <span className="text-sm text-gray-700">Solo asignados a mí</span>
            </label>
            <select
              value={filtroLeadEstado}
              onChange={(e) => setFiltroLeadEstado(e.target.value)}
              className="text-sm border rounded px-2 py-1"
            >
              {estadosLeads.map(estado => (
                <option key={estado} value={estado}>
                  {estado === 'todos' ? 'Todos' : estado.charAt(0).toUpperCase() + estado.slice(1)}
                </option>
              ))}
            </select>
          </div>

          {/* Lista de Leads */}
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {leadsFiltrados.map((lead) => (
              <div
                key={lead.id}
                onClick={() => navigate(`/crm/leads/${lead.id}`)}
                className="p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{lead.nombre}</p>
                    {lead.empresa && (
                      <p className="text-sm text-gray-500">{lead.empresa}</p>
                    )}
                    {lead.email && (
                      <p className="text-xs text-gray-400">{lead.email}</p>
                    )}
                  </div>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    lead.estado === 'nuevo' ? 'bg-blue-100 text-blue-800' :
                    lead.estado === 'contactado' ? 'bg-yellow-100 text-yellow-800' :
                    lead.estado === 'calificado' ? 'bg-green-100 text-green-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {lead.estado}
                  </span>
                </div>
              </div>
            ))}
            {leadsFiltrados.length === 0 && (
              <p className="text-gray-500 text-center py-8">No hay leads con estos filtros</p>
            )}
          </div>
        </div>

        {/* Panel de Oportunidades */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-800">Oportunidades</h2>
            <button
              onClick={() => navigate('/crm/oportunidades')}
              className="text-primary-600 hover:text-primary-700 text-sm font-medium"
            >
              Ver todas →
            </button>
          </div>

          {/* Indicadores de Oportunidades */}
          <div className="grid grid-cols-3 gap-2 mb-4">
            <div
              onClick={() => setFiltroOportunidadEstado('activa')}
              className={`p-2 rounded-lg cursor-pointer transition ${
                filtroOportunidadEstado === 'activa' ? 'bg-green-100 border-2 border-green-500' : 'bg-gray-50 border-2 border-transparent'
              }`}
            >
              <p className="text-xs text-gray-600">Activas</p>
              <p className="text-lg font-bold text-green-600">{oportunidadesActivas}</p>
            </div>
            <div className="p-2 rounded-lg bg-gray-50">
              <p className="text-xs text-gray-600">Total</p>
              <p className="text-lg font-bold text-gray-800">{totalOportunidades}</p>
            </div>
            <div className="p-2 rounded-lg bg-gray-50">
              <p className="text-xs text-gray-600">Valor</p>
              <p className="text-lg font-bold text-purple-600">
                ${valorPipeline.toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </p>
            </div>
          </div>

          {/* Filtros adicionales */}
          <div className="flex flex-col gap-2 mb-4 pb-4 border-b">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={filtroOportunidadAsignado}
                onChange={(e) => setFiltroOportunidadAsignado(e.target.checked)}
                className="rounded"
              />
              <span className="text-sm text-gray-700">Solo asignadas a mí</span>
            </label>
            <div className="flex gap-2">
              <select
                value={filtroOportunidadEstado}
                onChange={(e) => setFiltroOportunidadEstado(e.target.value)}
                className="text-sm border rounded px-2 py-1 flex-1"
              >
                {estadosOportunidades.map(estado => (
                  <option key={estado} value={estado}>
                    {estado === 'todos' ? 'Todos' : estado.charAt(0).toUpperCase() + estado.slice(1)}
                  </option>
                ))}
              </select>
              <select
                value={filtroOportunidadEtapa}
                onChange={(e) => setFiltroOportunidadEtapa(e.target.value === 'todos' ? 'todos' : Number(e.target.value))}
                className="text-sm border rounded px-2 py-1 flex-1"
              >
                <option value="todos">Todas las etapas</option>
                {etapas
                  .filter(e => e.isActive)
                  .sort((a, b) => a.orden - b.orden)
                  .map(etapa => (
                    <option key={etapa.id} value={etapa.id}>
                      {etapa.nombre}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          {/* Lista de Oportunidades */}
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {oportunidadesFiltradas.map((oportunidad) => (
              <div
                key={oportunidad.id}
                onClick={() => navigate(`/crm/oportunidades/${oportunidad.id}`)}
                className="p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{oportunidad.nombre}</p>
                    {oportunidad.cliente && (
                      <p className="text-sm text-gray-500">{oportunidad.cliente.nombre}</p>
                    )}
                    <p className="text-sm font-medium text-green-600">
                      ${(typeof oportunidad.valor === 'string' ? parseFloat(oportunidad.valor) : (oportunidad.valor || 0)).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span
                      className="px-2 py-1 text-xs rounded-full text-white"
                      style={{ backgroundColor: oportunidad.etapa?.color || '#3B82F6' }}
                    >
                      {oportunidad.etapa?.nombre || 'Sin etapa'}
                    </span>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      oportunidad.estado === 'activa' ? 'bg-green-100 text-green-800' :
                      oportunidad.estado === 'ganada' ? 'bg-blue-100 text-blue-800' :
                      oportunidad.estado === 'perdida' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {oportunidad.estado}
                    </span>
                  </div>
                </div>
              </div>
            ))}
            {oportunidadesFiltradas.length === 0 && (
              <p className="text-gray-500 text-center py-8">No hay oportunidades con estos filtros</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MiTrabajo;

